import { useEffect } from "react";

/**
 * Globally disables text selection, copy/cut, right-click context menu,
 * drag, and common copy/save/devtool keyboard shortcuts.
 *
 * Allowed exceptions (still copyable / selectable):
 * - Form fields: input, textarea, contenteditable
 * - Email addresses: any <a href="mailto:..."> or element with [data-copyable="true"]
 */
const CopyProtection = () => {
  useEffect(() => {
    const isEditable = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        el.isContentEditable === true
      );
    };

    const isCopyable = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el || typeof el.closest !== "function") return false;
      return !!el.closest('[data-copyable="true"], a[href^="mailto:"]');
    };

    const block = (e: Event) => {
      if (isEditable(e.target) || isCopyable(e.target)) return;
      e.preventDefault();
    };

    const blockKeys = (e: KeyboardEvent) => {
      if (isEditable(e.target) || isCopyable(e.target)) return;
      const k = e.key.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;

      // Copy / Cut / Select-all / Save / Print / View-source
      if (mod && ["c", "x", "a", "s", "p", "u"].includes(k)) {
        e.preventDefault();
        return;
      }
      // DevTools shortcuts
      if (k === "f12") {
        e.preventDefault();
        return;
      }
      if (mod && e.shiftKey && ["i", "j", "c"].includes(k)) {
        e.preventDefault();
      }
    };

    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("contextmenu", block);
    document.addEventListener("selectstart", block);
    document.addEventListener("dragstart", block);
    document.addEventListener("keydown", blockKeys);

    const style = document.createElement("style");
    style.setAttribute("data-copy-protection", "true");
    style.textContent = `
      html, body {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
      }
      input, textarea, [contenteditable="true"],
      [data-copyable="true"], [data-copyable="true"] *,
      a[href^="mailto:"], a[href^="mailto:"] * {
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
        -webkit-touch-callout: default !important;
      }
      img { -webkit-user-drag: none; user-drag: none; }
    `;
    document.head.appendChild(style);

    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("selectstart", block);
      document.removeEventListener("dragstart", block);
      document.removeEventListener("keydown", blockKeys);
      style.remove();
    };
  }, []);

  return null;
};

export default CopyProtection;
